import React, { Dispatch, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer, useImmerReducer } from "use-immer";

import { IgTab, IgTabPanel, IgTabs, IgTypography } from "@intelligems/ig-theme";
import { StripeApiGetSubscriptionByIdResponseBodyType } from "@intelligems/ig-types";
import { Box, Drawer, Grid } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { IgAlertSimple } from "@src/components/Utils/IgAlertSimple";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { PhaseSettings } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PhaseSettingsTab/PhaseSettings";
import { PhaseSettingsTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PhaseSettingsTab/PhaseSettingsTab";
import { PhasesMode } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PhasesMode/PhasesMode";
import { PricesTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/PricesTab";
import { ProductsTab } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/ProductsTab/ProductsTab";
import { SettingsSection } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/SettingsSection/SettingsSection";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
  initialCreateSubscriptionReducerState,
  SubscriptionReducer,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { getDrawer, setDrawer } from "@src/store/slices/drawers";
import { ModifySubscriptionDrawerState } from "@src/store/types/drawers";

interface ContentProps {
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
}

const IncompleteAlert = () => {
  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Grid container mt={2}>
        <Grid item flex={1}>
          <IgAlertSimple
            severity={"warning"}
            message={
              <IgTypography>
                <IgTypography sx={{ fontWeight: 600 }}>
                  Subscription Incomplete
                </IgTypography>
                <br />
                Stripe does not allow modifications to incomplete subscriptions.
                The customer likely needs to pay their invoice.
              </IgTypography>
            }
            alertKey={"incomplete-sub"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const ContentWrapper = () => {
  const [isReady, setIsReady] = useImmer<boolean>(false);
  const drawer: ModifySubscriptionDrawerState | undefined = useSelector(
    getDrawer("modifySubscription")
  );
  const subscription =
    useStripeApi<StripeApiGetSubscriptionByIdResponseBodyType>(
      {
        action: "getSubscriptionById",
        orgs: drawer?.orgs || [],
        stripeSubscriptionId: drawer?.stripeSubscriptionId ?? "",
        subscriptionLinkIds: drawer?.subscriptionLinkIds || [],
      },
      !!(
        drawer &&
        drawer.open &&
        drawer.stripeSubscriptionId &&
        drawer.orgs &&
        drawer.subscriptionLinkIds
      )
    );

  const [payload, dispatchPayload] = useImmerReducer(
    SubscriptionReducer,
    initialCreateSubscriptionReducerState
  );

  useEffect(() => {
    if (!drawer) {
      return;
    }

    if (drawer.open) {
      if (subscription.isSuccess) {
        const p = subscription.data.subscription.type === "withPhases" ? -1 : 0;

        dispatchPayload({
          type: "initModify",
          value: {
            usages: {
              ordersPerMonth: 0,
            },
            agencyId: null,
            mode: "modify",
            orgs: drawer.orgs,
            stripeSubscriptionId: drawer.stripeSubscriptionId!,
            subscriptionLinkIds: drawer.subscriptionLinkIds,
            tabIndex: 0,
            ...subscription.data.subscription,
            phaseIndex: p,
            warnings: {},
            pendingChanges: false,
            cancelAt: subscription.data.subscription.cancelAt || null,
          },
        });
        dispatchPayload({
          type: "setUsages",
          value: drawer.usages,
        });
        setIsReady(true);
      }
    } else {
      dispatchPayload({
        type: "setOrgs",
        value: drawer.orgs,
      });
      dispatchPayload({
        type: "setAgencyId",
        value: drawer.agencyId,
      });
      dispatchPayload({
        type: "setUsages",
        value: drawer.usages,
      });
      setIsReady(false);
    }
  }, [drawer, subscription.isSuccess, subscription.data]);

  if (!subscription.isSuccess) {
    return <SkeletonGroup lines={4} />;
  }

  if (subscription.data.subscription.type === "withPhases") {
    if (payload.phaseIndex === -1) {
      return <PhasesMode payload={payload} dispatchPayload={dispatchPayload} />;
    }
  }

  if (isReady && payload.phaseIndex > -1) {
    return <Content payload={payload} dispatchPayload={dispatchPayload} />;
  }
  return <SkeletonGroup lines={4} />;
};

const Content = ({ payload, dispatchPayload }: ContentProps) => {
  if (payload.status === "incomplete") {
    return <IncompleteAlert />;
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <IgTabs
          value={payload.tabIndex}
          onChange={(_e, value: number) => {
            dispatchPayload({ type: "setTabIndex", value });
          }}
        >
          <IgTab label="Products" />
          <IgTab label="Prices" />
          <IgTab label="Phase Settings" />
        </IgTabs>
      </Box>
      <IgTabPanel index={0} value={payload.tabIndex}>
        <ProductsTab
          index={0}
          payload={payload}
          dispatchPayload={dispatchPayload}
          setPhase={payload.type === "withPhases" ? dispatchPayload : undefined}
        />
      </IgTabPanel>
      <IgTabPanel index={1} value={payload.tabIndex}>
        {payload.phases[payload.phaseIndex].items.length ? (
          <PricesTab
            index={1}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        ) : null}
      </IgTabPanel>
      <IgTabPanel index={2} value={payload.tabIndex}>
        {payload.phases[payload.phaseIndex].items.length ? (
          <PhaseSettingsTab
            index={2}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        ) : null}
      </IgTabPanel>
    </>
  );
};

export const ModifySubscriptionDrawer = () => {
  const drawer = useSelector(getDrawer("modifySubscription"))!;

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(
      setDrawer({
        type: "modifySubscription",
        open: false,
        agencyId: null,
        stripeSubscriptionId: "",
        subscriptionLinkIds: [""],
        orgs: [],
        usages: null,
      })
    );
  };

  if (drawer?.open && !drawer?.orgs?.length) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={drawer?.open || false}
      onClose={() => handleClose()}
      sx={{
        zIndex: 100,
        ".MuiDrawer-paper": { borderRadius: "0 !important" },
      }}
    >
      <Grid
        direction={"column"}
        container
        sx={{ width: 800, p: 3 }}
        rowSpacing={3}
      >
        <Grid item sx={{ marginBottom: "20px !important" }}>
          <IgTypography>Modify Subscription</IgTypography>
        </Grid>
        <ContentWrapper />
      </Grid>
    </Drawer>
  );
};
